// Companion shell — the Home launcher's interactions: tile routing, the
// account / linked-device sheet, theme switch, on-phone unlink, and the
// "coming soon" overlay. Built on top of the scan state-machine in app.ts.
import { showScreen, show, resetPages, resetIdentity } from "./app";
import { clearSnaps, loadSnap } from "./cache";
import { vmReset } from "./voicememo";
import { loadContacts } from "./contacts";
import { loadFaq } from "./faq";
import { loadTasks } from "./tasks";
import { loadMinutes } from "./minutes";
import { loadForms } from "./voiceforms";
import { loadLedger } from "./receipts";
import { loadDocs } from "./docs";
import { loadQuotes } from "./quotes";

const $ = <T extends Element = HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);

// Coming-soon copy per tile. Tasks/Contacts/FAQ/Minutes already exist in the
// Outlook add-in (their phone screens land in a later step); Receipts/VoiceForms
// aren't built anywhere yet.
// All eight tiles are live — the "coming soon" overlay is retired (kept in the DOM
// for any future tile; nothing routes to it today).

// ── Home tile count badges (desktop-PWA parity) ──
// Green bubble on the icon chip = the module's live count, fed by the same snapshots
// that paint the lists (tasks = OPEN tasks, like the desktop Home). Re-runs on every
// rp:snap, i.e. whenever any module saves a fresh list.
function updateTileBadges(): void {
  const set = (key: string, n: number): void => {
    const el = document.querySelector<HTMLElement>(`.tt-badge[data-badge="${key}"]`);
    if (!el) return;
    el.textContent = n > 99 ? "99+" : String(n);
    el.hidden = n <= 0;
  };
  set("contacts", (loadSnap<unknown[]>("contacts") || []).length);
  set("tasks", (loadSnap<{ tasks?: { status?: string }[] }>("tasks")?.tasks || []).filter((t) => t.status !== "done").length);
  // receipts snapshot = {documents, projects, …} since the ledger editor landed; tolerate the old array shape
  const rcSnap = loadSnap<{ documents?: unknown[] } | unknown[]>("receipts");
  set("receipts", (Array.isArray(rcSnap) ? rcSnap : rcSnap?.documents || []).length);
  set("minutes", (loadSnap<unknown[]>("minutes") || []).length);
  // docs list is capped at 100 server-side — prefer the TRUE total the loader snapshots
  set("docs", loadSnap<number>("docs-total") ?? (loadSnap<unknown[]>("docs") || []).length);
  set("quotes", (loadSnap<unknown[]>("quotes") || []).length);
  set("faq", (loadSnap<unknown[]>("faq") || []).length);
  set("voiceforms", (loadSnap<unknown[]>("voiceforms") || []).length);
}

function openAccount(): void {
  $("#setScrim")?.classList.add("open");
  $("#setSheet")?.classList.add("open");
}
function closeAccount(): void {
  $("#setScrim")?.classList.remove("open");
  $("#setSheet")?.classList.remove("open");
}

function setTheme(theme: "dark" | "light"): void {
  document.documentElement.setAttribute("data-theme", theme);
  document.body.classList.toggle("theme-light-bg", theme === "light");
  localStorage.setItem("rplay-theme", theme);
  syncThemeSeg();
}
function syncThemeSeg(): void {
  const cur = document.documentElement.getAttribute("data-theme") || "dark";
  document.querySelectorAll<HTMLElement>("#themeSeg [data-theme-set]").forEach((b) =>
    b.classList.toggle("on", b.getAttribute("data-theme-set") === cur),
  );
}

// On-phone unlink (§4.5): revoke this device's session + drop to the pairing
// screen. (Revoking a LOST phone from the pane is a separate workstream.)
async function unlink(): Promise<void> {
  const ok = window.confirm("Unlink this phone from your Outlook inbox?\n\nIt will no longer access your data. To reconnect, scan the QR code in Outlook → Tools → DocScanner again.");
  if (!ok) return;
  try { await fetch("/api/session", { method: "DELETE", credentials: "include" }); } catch { /* ignore */ }
  clearSnaps(); // cached lists belong to the unlinked account
  closeAccount();
  resetIdentity();
  showScreen("scan");
  show("pairing");
}

export function initShell(): void {
  syncThemeSeg();
  updateTileBadges();
  document.addEventListener("rp:snap", updateTileBadges);

  // tile grid → route to the tool (or explain a not-yet-on-phone tile)
  $(".tool-grid")?.addEventListener("click", (e) => {
    const tile = (e.target as HTMLElement).closest<HTMLElement>("[data-tile]");
    if (!tile) return;
    const key = tile.getAttribute("data-tile") || "";
    if (key === "scan") { showScreen("scan"); resetPages(); show("capture"); }
    else if (key === "voicememo") { showScreen("voicememo"); vmReset(); }
    else if (key === "contacts") { document.getElementById("ctDetail")?.classList.remove("open"); showScreen("contacts"); void loadContacts(); }
    else if (key === "faq") { document.getElementById("faqDetail")?.classList.remove("open"); showScreen("faq"); void loadFaq(); }
    else if (key === "tasks") { document.getElementById("tkEditor")?.classList.remove("open"); showScreen("tasks"); void loadTasks(); }
    else if (key === "minutes") { document.getElementById("mnEditor")?.classList.remove("open"); showScreen("minutes"); void loadMinutes(); }
    else if (key === "docs") { document.getElementById("docsDetail")?.classList.remove("open"); showScreen("docs"); void loadDocs(); }
    else if (key === "quotes") { showScreen("quotes"); void loadQuotes(); }
    else if (key === "receipts") { document.getElementById("rcEditor")?.classList.remove("open"); showScreen("receipts"); void loadLedger(); }
    else if (key === "voiceforms") { document.getElementById("vfRun")?.classList.remove("open"); document.getElementById("vfReview")?.classList.remove("open"); showScreen("voiceforms"); void loadForms(); }
  });

  // account / linked-device sheet (logo account button + the linked chip)
  document.querySelectorAll<HTMLElement>('[data-act="openAccount"]').forEach((b) =>
    b.addEventListener("click", openAccount),
  );
  $("#setScrim")?.addEventListener("click", closeAccount);

  // theme switch
  $("#themeSeg")?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-theme-set]");
    if (b) setTheme(b.getAttribute("data-theme-set") === "light" ? "light" : "dark");
  });

  // unlink + soon-overlay close
  $("#unlinkBtn")?.addEventListener("click", () => void unlink());
  $("#soonClose")?.addEventListener("click", () => $("#soonOv")?.classList.remove("open"));
}
